import React, { useState, useEffect } from 'react'
import { logout } from '../services/auth'
import { getTenants, createTenant, suspendTenant, activateTenant, updateTenant, getUsersByTenant, getTenantStats, addUserToTenant, createUserInTenant, getAuditLogs, getTenantMetricsConfig, saveTenantMetricsConfig, updateTenantModes, logTenantAudit } from '../services/api'

const TRANSPORT_MODES = [{ key: 'ocean', label: 'Marítimo' }, { key: 'land', label: 'Terrestre' }, { key: 'air', label: 'Aéreo' }]

const OCEAN_DEFAULT_CONFIG = {
  proximity_buckets: { near_days: 3, warning_days: 10 },
  eta_alert_threshold_days: 1,
  empty_return: { amber_days_before: 3 },
  stale_data_days: 2
}

const COLORS = {
  primary: '#d4602a',
  secondary: '#3d7a5e',
  bg: '#e8e4dc',
  card: '#f5f2ec',
  border: '#ddd9d0',
  sidebar: '#2d2d2d'
}

function formatMoney(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function StatusBadge({ status }) {
  const colors = {
    active: { bg: '#d1fae5', color: '#065f46' },
    suspended: { bg: '#fee2e2', color: '#dc2626' },
    expired: { bg: '#f3f4f6', color: '#374151' }
  }
  const s = colors[status] || colors.expired
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {status}
    </span>
  )
}

function PlanBadge({ plan }) {
  const colors = {
    basic: { bg: '#dbeafe', color: '#1d4ed8' },
    professional: { bg: '#ede9fe', color: '#6d28d9' },
    enterprise: { bg: '#fef3c7', color: '#d97706' }
  }
  const s = colors[plan] || colors.basic
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {plan}
    </span>
  )
}

export default function Dashboard({ user, onLogout }) {
  const [page, setPage] = useState('tenants')
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(false)
  const [metricsText, setMetricsText] = useState('')
  const [metricsMsg, setMetricsMsg] = useState('')
  const [newExpiry, setNewExpiry] = useState('')
  const [showCreateTenant, setShowCreateTenant] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [tenantUsers, setTenantUsers] = useState([])
  const [tenantStats, setTenantStats] = useState(null)
  const [tenantForm, setTenantForm] = useState({
    companyName: '', contactEmail: '', licensePlan: 'basic',
    maxUsers: 3, expiresAt: '', notes: '', businessModel: 'freight_broker', enabledModes: ['ocean']
  })
  const [tenantFormError, setTenantFormError] = useState('')
  const [tenantFormLoading, setTenantFormLoading] = useState(false)
  const [modesSel, setModesSel] = useState(['ocean'])
  const [modesMsg, setModesMsg] = useState('')
  const [trackingProvider, setTrackingProvider] = useState('none')
  const [trackingMsg, setTrackingMsg] = useState('')
  // LOG-3: capacidades de logística por tenant (no depende del business_model)
  const [log3Flags, setLog3Flags] = useState({ whatsapp_channel_enabled: false, require_delivery_verification: false })
  const [log3Msg, setLog3Msg] = useState('')
  // DIST: capacidades de distribución por tenant (flags, no dependen del business_model)
  const [distFlags, setDistFlags] = useState({ fleet_management_enabled: false, delivery_points_enabled: false, route_planning_enabled: false, cod_enabled: false })
  const [docFlags, setDocFlags] = useState({ document_requirements_enabled: false, cold_chain_enabled: false })
  const [docMsg, setDocMsg] = useState('')
  const [distRadius, setDistRadius] = useState('')  // delivery_geo_radius_m (metros; vacío = sin validación)
  const [distSla, setDistSla] = useState('')        // sla_target_minutes (DIST-5)
  const [distBaseline, setDistBaseline] = useState('')  // failure_baseline_pct (DIST-5)
  const [distMsg, setDistMsg] = useState('')
  const [showAddUser, setShowAddUser] = useState(false)
  const [addUserError, setAddUserError] = useState('')
  const [addUserLoading, setAddUserLoading] = useState(false)
  const [addUserForm, setAddUserForm] = useState({ email: '', password: '', fullName: '', role: 'agent' })
  const [auditLogs, setAuditLogs] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)

  useEffect(() => {
    loadTenants()
  }, [])

  useEffect(() => {
    if (page === 'audit') loadAuditLogs()
  }, [page])

  async function loadTenants() {
    setLoading(true)
    try {
      const data = await getTenants()
      setTenants(data)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  async function loadAuditLogs() {
    setAuditLoading(true)
    try {
      const data = await getAuditLogs()
      setAuditLogs(data)
    } catch (e) {
      console.error(e)
    }
    setAuditLoading(false)
  }

  async function handleCreateTenant() {
    setTenantFormError('')
    setTenantFormLoading(true)
    try {
      if (!tenantForm.companyName) throw new Error('Company name is required')
      if (!tenantForm.contactEmail) throw new Error('Contact email is required')
      if (tenantForm.businessModel === 'ocean_forwarding' && (!tenantForm.enabledModes || tenantForm.enabledModes.length === 0))
        throw new Error('Selecciona al menos un modo de transporte')
      await createTenant(tenantForm)
      setShowCreateTenant(false)
      setTenantForm({ companyName: '', contactEmail: '', licensePlan: 'basic', maxUsers: 3, expiresAt: '', notes: '', businessModel: 'freight_broker', enabledModes: ['ocean'] })
      loadTenants()
    } catch (e) {
      setTenantFormError(e.message)
    }
    setTenantFormLoading(false)
  }

  async function handleSuspend(tenantId) {
    if (!window.confirm('Are you sure you want to suspend this license?')) return
    try {
      await suspendTenant(tenantId)
      loadTenants()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function handleActivate(tenantId) {
    try {
      await activateTenant(tenantId)
      loadTenants()
    } catch (e) { alert('Error: ' + e.message) }
  }

  // Renovar / cambiar la fecha de caducidad de la licencia y reactivar.
  async function handleSetExpiry(expiresAt) {
    try {
      await updateTenant(selectedTenant.tenant_id, { expires_at: expiresAt, license_status: 'active' })
      const updated = { ...selectedTenant, expires_at: expiresAt, license_status: 'active' }
      setSelectedTenant(updated)
      setNewExpiry('')
      loadTenants()
    } catch (e) { alert('Error: ' + e.message) }
  }
  function renewMonths(months) {
    const d = new Date(); d.setMonth(d.getMonth() + months)
    handleSetExpiry(d.toISOString().slice(0, 10))
  }

  // Activar/desactivar el asistente de IA (Fase IA de PyroDesk) por tenant.
  async function handleToggleAi(tenant) {
    const turningOn = !tenant.ai_enabled
    if (!window.confirm(turningOn
      ? 'Enable the AI assistant for this tenant? (billed per usage)'
      : 'Disable the AI assistant for this tenant?')) return
    try {
      await updateTenant(tenant.tenant_id, { ai_enabled: turningOn })
      loadTenants()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function openTenantDetail(tenant) {
    setSelectedTenant(tenant)
    setPage('tenant-detail')
    setLog3Flags({ whatsapp_channel_enabled: !!tenant.whatsapp_channel_enabled, require_delivery_verification: !!tenant.require_delivery_verification })
    setLog3Msg('')
    setDistFlags({ fleet_management_enabled: !!tenant.fleet_management_enabled, delivery_points_enabled: !!tenant.delivery_points_enabled, route_planning_enabled: !!tenant.route_planning_enabled, cod_enabled: !!tenant.cod_enabled })
    setDocFlags({ document_requirements_enabled: !!tenant.document_requirements_enabled, cold_chain_enabled: !!tenant.cold_chain_enabled }); setDocMsg('')
    setDistRadius(tenant.delivery_geo_radius_m != null ? String(tenant.delivery_geo_radius_m) : '')
    setDistSla(tenant.sla_target_minutes != null ? String(tenant.sla_target_minutes) : '')
    setDistBaseline(tenant.failure_baseline_pct != null ? String(tenant.failure_baseline_pct) : '')
    setDistMsg('')
    try {
      const [users, stats] = await Promise.all([
        getUsersByTenant(tenant.tenant_id),
        getTenantStats(tenant.tenant_id)
      ])
      setTenantUsers(users)
      setTenantStats(stats)
    } catch (e) { console.error(e) }
    setMetricsMsg('')
    setModesMsg('')
    if (tenant.business_model === 'ocean_forwarding') {
      setModesSel(tenant.enabled_transport_modes && tenant.enabled_transport_modes.length ? tenant.enabled_transport_modes : ['ocean'])
      setTrackingProvider(tenant.tracking_provider || 'none'); setTrackingMsg('')
      try {
        const cfg = await getTenantMetricsConfig(tenant.tenant_id)
        setMetricsText(JSON.stringify(Object.keys(cfg || {}).length ? cfg : OCEAN_DEFAULT_CONFIG, null, 2))
      } catch (e) { setMetricsText(JSON.stringify(OCEAN_DEFAULT_CONFIG, null, 2)) }
    }
  }

  async function handleSaveTrackingProvider() {
    setTrackingMsg('')
    try {
      await updateTenant(selectedTenant.tenant_id, { tracking_provider: trackingProvider })
      await logTenantAudit(selectedTenant.tenant_id, 'update_tracking_provider', selectedTenant.tenant_id,
        `tracking_provider → ${trackingProvider}`)
      setSelectedTenant({ ...selectedTenant, tracking_provider: trackingProvider })
      setTrackingMsg('Guardado.'); loadTenants()
    } catch (e) { setTrackingMsg('Error: ' + e.message) }
  }

  async function handleSaveLog3Flags() {
    setLog3Msg('')
    const prev = { whatsapp_channel_enabled: !!selectedTenant.whatsapp_channel_enabled, require_delivery_verification: !!selectedTenant.require_delivery_verification }
    try {
      await updateTenant(selectedTenant.tenant_id, {
        whatsapp_channel_enabled: log3Flags.whatsapp_channel_enabled,
        require_delivery_verification: log3Flags.require_delivery_verification
      })
      await logTenantAudit(selectedTenant.tenant_id, 'update_log3_flags', selectedTenant.tenant_id,
        `whatsapp_channel_enabled: ${prev.whatsapp_channel_enabled} → ${log3Flags.whatsapp_channel_enabled}; require_delivery_verification: ${prev.require_delivery_verification} → ${log3Flags.require_delivery_verification}`)
      setSelectedTenant({ ...selectedTenant, ...log3Flags })
      setLog3Msg('Guardado.'); loadTenants()
    } catch (e) { setLog3Msg('Error: ' + e.message) }
  }

  async function handleSaveDistFlags() {
    setDistMsg('')
    const prev = { fleet_management_enabled: !!selectedTenant.fleet_management_enabled, delivery_points_enabled: !!selectedTenant.delivery_points_enabled, route_planning_enabled: !!selectedTenant.route_planning_enabled, cod_enabled: !!selectedTenant.cod_enabled }
    try {
      const radiusValue = distRadius === '' ? null : Number(distRadius)
      const slaValue = distSla === '' ? null : Number(distSla)
      const baselineValue = distBaseline === '' ? null : Number(distBaseline)
      await updateTenant(selectedTenant.tenant_id, {
        fleet_management_enabled: distFlags.fleet_management_enabled,
        delivery_points_enabled: distFlags.delivery_points_enabled,
        route_planning_enabled: distFlags.route_planning_enabled,
        cod_enabled: distFlags.cod_enabled,
        delivery_geo_radius_m: radiusValue,
        sla_target_minutes: slaValue,
        failure_baseline_pct: baselineValue
      })
      await logTenantAudit(selectedTenant.tenant_id, 'update_dist_flags', selectedTenant.tenant_id,
        `fleet_management_enabled: ${prev.fleet_management_enabled} → ${distFlags.fleet_management_enabled}; delivery_points_enabled: ${prev.delivery_points_enabled} → ${distFlags.delivery_points_enabled}; route_planning_enabled: ${prev.route_planning_enabled} → ${distFlags.route_planning_enabled}; cod_enabled: ${prev.cod_enabled} → ${distFlags.cod_enabled}; delivery_geo_radius_m → ${radiusValue}; sla_target_minutes → ${slaValue}; failure_baseline_pct → ${baselineValue}`)
      setSelectedTenant({ ...selectedTenant, ...distFlags, delivery_geo_radius_m: radiusValue, sla_target_minutes: slaValue, failure_baseline_pct: baselineValue })
      setDistMsg('Guardado.'); loadTenants()
    } catch (e) { setDistMsg('Error: ' + e.message) }
  }

  async function handleSaveDocFlags() {
    setDocMsg('')
    const prev = { document_requirements_enabled: !!selectedTenant.document_requirements_enabled, cold_chain_enabled: !!selectedTenant.cold_chain_enabled }
    try {
      await updateTenant(selectedTenant.tenant_id, {
        document_requirements_enabled: docFlags.document_requirements_enabled,
        cold_chain_enabled: docFlags.cold_chain_enabled
      })
      await logTenantAudit(selectedTenant.tenant_id, 'update_doc_flags', selectedTenant.tenant_id,
        `document_requirements_enabled: ${prev.document_requirements_enabled} → ${docFlags.document_requirements_enabled}; cold_chain_enabled: ${prev.cold_chain_enabled} → ${docFlags.cold_chain_enabled}`)
      setSelectedTenant({ ...selectedTenant, ...docFlags })
      setDocMsg('Guardado.'); loadTenants()
    } catch (e) { setDocMsg('Error: ' + e.message) }
  }

  async function handleSaveModes() {
    setModesMsg('')
    if (!modesSel.length) { setModesMsg('Error: al menos un modo'); return }
    const prev = selectedTenant.enabled_transport_modes || ['ocean']
    const removed = prev.filter(m => !modesSel.includes(m))
    if (removed.length) {
      const labels = removed.map(m => (TRANSPORT_MODES.find(x => x.key === m) || {}).label || m).join(', ')
      if (!window.confirm(`Vas a desactivar: ${labels}. Los datos de ese modo se OCULTAN, no se borran, y reaparecen al reactivarlo. ¿Continuar?`)) return
    }
    try {
      await updateTenantModes(selectedTenant.tenant_id, modesSel)
      await logTenantAudit(selectedTenant.tenant_id, 'update_transport_modes', selectedTenant.tenant_id,
        `modos: ${prev.join(',')} → ${modesSel.join(',')}`)
      setSelectedTenant({ ...selectedTenant, enabled_transport_modes: modesSel })
      setModesMsg('Guardado.')
      loadTenants()
    } catch (e) { setModesMsg('Error: ' + e.message) }
  }
  async function handleSaveMetrics() {
    setMetricsMsg('')
    try {
      const parsed = JSON.parse(metricsText)
      await saveTenantMetricsConfig(selectedTenant.tenant_id, parsed)
      setMetricsMsg('Saved.')
    } catch (e) { setMetricsMsg('Error: ' + e.message) }
  }

  async function handleLogout() {
    await logout()
    onLogout()
  }

  const menuItems = [
    { id: 'tenants', label: 'Tenants' },
    { id: 'overview', label: 'Overview' },
    { id: 'audit', label: 'Audit Logs' },
  ]

  const activeTenants = tenants.filter(t => t.license_status === 'active').length
  const suspendedTenants = tenants.filter(t => t.license_status === 'suspended').length

  async function handleAddUser(useAutoCreate) {
    setAddUserError('')
    setAddUserLoading(true)
    try {
      if (!addUserForm.email) throw new Error('Email is required')
      
      if (useAutoCreate) {
        // Opción B — crear automáticamente
        if (!addUserForm.password || addUserForm.password.length < 6) throw new Error('Password must be at least 6 characters')
        await createUserInTenant(selectedTenant.tenant_id, addUserForm.email, addUserForm.password, addUserForm.role, addUserForm.fullName)
      } else {
        // Opción A — asignar usuario existente
        await addUserToTenant(selectedTenant.tenant_id, addUserForm.email, addUserForm.role, addUserForm.fullName)
      }
      
      setShowAddUser(false)
      setAddUserForm({ email: '', password: '', fullName: '', role: 'agent' })
      const users = await getUsersByTenant(selectedTenant.tenant_id)
      setTenantUsers(users)
    } catch (e) {
      setAddUserError(e.message)
    }
    setAddUserLoading(false)
  }

  async function runBackup() {
    try {
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/backup-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      if (data.success) {
        alert('Backup completed successfully. Check your email.')
      } else {
        alert('Backup error: ' + data.error)
      }
    } catch (e) {
      alert('Error: ' + e.message)
    }
  }

  return (
    <div style={{ display: 'flex', fontFamily: "'Segoe UI', sans-serif", minHeight: '100vh' }}>

      {/* SIDEBAR */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, width: 220, height: '100vh',
        background: COLORS.sidebar, display: 'flex', flexDirection: 'column',
        padding: '24px 0', zIndex: 10
      }}>
        <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, padding: '0 24px 8px' }}>
          PYRO <span style={{ color: COLORS.primary }}>ADMIN</span>
        </div>
        <div style={{ color: '#888', fontSize: 11, padding: '0 24px 16px', borderBottom: '1px solid #444' }}>
          {user?.email}
        </div>
        <div style={{ height: 2, background: `linear-gradient(to right, ${COLORS.secondary}, ${COLORS.primary}, #c9a0dc, ${COLORS.secondary})`, margin: '0 24px 16px', borderRadius: 2 }} />
        <ul style={{ listStyle: 'none', flex: 1, margin: 0, padding: 0 }}>
          {menuItems.map(item => (
            <li key={item.id}>
              <button onClick={() => setPage(item.id)} style={{
                display: 'block', width: '100%', padding: '11px 24px',
                color: page === item.id || (page === 'tenant-detail' && item.id === 'tenants') ? '#fff' : '#aaa',
                background: page === item.id || (page === 'tenant-detail' && item.id === 'tenants') ? COLORS.secondary : 'transparent',
                borderLeft: page === item.id || (page === 'tenant-detail' && item.id === 'tenants') ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                border: 'none', textAlign: 'left', fontSize: 14, cursor: 'pointer'
              }}>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #444' }}>
          <button onClick={handleLogout} style={{
            width: '100%', padding: 10, background: 'transparent',
            border: '1px solid #555', color: '#aaa', borderRadius: 8,
            cursor: 'pointer', fontSize: 13
          }}>
            Log Out
          </button>
        </div>
      </nav>

      {/* MAIN */}
      <main style={{ marginLeft: 220, padding: 32, width: 'calc(100% - 220px)', background: COLORS.bg, minHeight: '100vh' }}>

        {loading && <div style={{ color: '#888', fontSize: 14 }}>Loading...</div>}

        {/* OVERVIEW */}
        {page === 'overview' && (
          <>
            <h1 style={{ fontSize: 24, marginBottom: 24 }}>Overview</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Total Tenants</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.primary }}>{tenants.length}</div>
              </div>
              <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Active Licenses</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.secondary }}>{activeTenants}</div>
              </div>
              <div style={{ background: COLORS.card, borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Suspended</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#dc2626' }}>{suspendedTenants}</div>
              </div>
            </div>
            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                <h2 style={{ fontSize: 16 }}>Licenses by Plan</h2>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#ede9e0' }}>
                    {['Plan', 'Count'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: '#777', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {['basic', 'professional', 'enterprise'].map(plan => (
                    <tr key={plan} style={{ borderTop: `1px solid ${COLORS.bg}` }}>
                      <td style={{ padding: '12px 16px' }}><PlanBadge plan={plan} /></td>
                      <td style={{ padding: '12px 16px', fontWeight: 600 }}>{tenants.filter(t => t.license_plan === plan).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={runBackup} style={{
                background: COLORS.secondary, color: '#fff', border: 'none',
                borderRadius: 8, padding: '10px 20px', fontSize: 13,
                fontWeight: 600, cursor: 'pointer'
              }}>Run Backup Now</button>
            </div>
          </>
        )}

        {/* TENANTS */}
        {page === 'tenants' && (
          <>
            <h1 style={{ fontSize: 24, marginBottom: 24 }}>Tenants</h1>
            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: 16 }}>All Tenants</h2>
                <button onClick={() => { setShowCreateTenant(true); setTenantFormError('') }} style={{
                  background: COLORS.primary, color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}>+ New Tenant</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 800 }}>
                  <thead>
                    <tr style={{ background: '#ede9e0' }}>
                      {['Company', 'Contact', 'Plan', 'Status', 'Max Users', 'Expires', 'Modes', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: '#777', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: 48, textAlign: 'center', color: '#aaa' }}>No tenants yet.</td></tr>
                    ) : tenants.map(t => (
                      <tr key={t.tenant_id} style={{ borderTop: `1px solid ${COLORS.bg}` }}>
                        <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                          <button onClick={() => openTenantDetail(t)} style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontWeight: 600, fontSize: 13, padding: 0 }}>
                            {t.company_name}
                          </button>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666' }}>{t.contact_email || '-'}</td>
                        <td style={{ padding: '12px 16px' }}><PlanBadge plan={t.license_plan} /></td>
                        <td style={{ padding: '12px 16px' }}><StatusBadge status={t.license_status} /></td>
                        <td style={{ padding: '12px 16px' }}>{t.max_users}</td>
                        <td style={{ padding: '12px 16px', color: '#666' }}>
                          {t.expires_at ? new Date(t.expires_at).toLocaleDateString('en-US') : 'No expiry'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', fontSize: 12 }}>
                          {t.business_model === 'ocean_forwarding'
                            ? (t.enabled_transport_modes || ['ocean']).map(m => (TRANSPORT_MODES.find(x => x.key === m) || {}).label || m).join(', ')
                            : '-'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {t.license_status === 'active' ? (
                              <button onClick={() => handleSuspend(t.tenant_id)} style={{
                                padding: '4px 10px', background: 'transparent', border: '1px solid #dc2626',
                                color: '#dc2626', borderRadius: 6, fontSize: 12, cursor: 'pointer'
                              }}>Suspend</button>
                            ) : (
                              <button onClick={() => handleActivate(t.tenant_id)} style={{
                                padding: '4px 10px', background: 'transparent', border: `1px solid ${COLORS.secondary}`,
                                color: COLORS.secondary, borderRadius: 6, fontSize: 12, cursor: 'pointer'
                              }}>Activate</button>
                            )}
                            <button onClick={() => handleToggleAi(t)} title="AI assistant (billed per usage)" style={{
                              padding: '4px 10px', background: t.ai_enabled ? 'rgba(212,96,42,0.15)' : 'transparent',
                              border: `1px solid ${t.ai_enabled ? '#D4602A' : '#888'}`,
                              color: t.ai_enabled ? '#D4602A' : '#888', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600
                            }}>{t.ai_enabled ? 'AI: On' : 'AI: Off'}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* AUDIT LOGS */}
        {page === 'audit' && (
          <>
            <h1 style={{ fontSize: 24, marginBottom: 24 }}>Audit Logs</h1>
            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: 16 }}>Recent Activity</h2>
                <button onClick={loadAuditLogs} style={{
                  background: 'transparent', border: `1px solid ${COLORS.border}`,
                  borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer'
                }}>Refresh</button>
              </div>
              {auditLoading ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#888' }}>Loading...</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 800 }}>
                    <thead>
                      <tr style={{ background: '#ede9e0' }}>
                        {['Timestamp', 'User', 'Action', 'Resource', 'Resource ID', 'Status', 'Details'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: '#777', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: 48, textAlign: 'center', color: '#aaa' }}>No audit logs yet.</td></tr>
                      ) : auditLogs.map(log => (
                        <tr key={log.id} style={{ borderTop: `1px solid ${COLORS.bg}` }}>
                          <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', fontSize: 12, color: '#666' }}>
                            {new Date(log.timestamp).toLocaleString('en-US')}
                          </td>
                          <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', fontSize: 12 }}>{log.user_email || '-'}</td>
                          <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                              fontSize: 11, fontWeight: 600,
                              background: log.action === 'CREATE' ? '#d1fae5' : log.action.includes('PAID') ? '#dbeafe' : '#ede9fe',
                              color: log.action === 'CREATE' ? '#065f46' : log.action.includes('PAID') ? '#1d4ed8' : '#6d28d9'
                            }}>
                              {log.action}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', fontSize: 12 }}>{log.resource || '-'}</td>
                          <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', fontSize: 12, color: COLORS.primary }}>{log.resource_id || '-'}</td>
                          <td style={{ padding: '10px 16px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                              fontSize: 11, fontWeight: 600,
                              background: log.success ? '#d1fae5' : '#fee2e2',
                              color: log.success ? '#065f46' : '#dc2626'
                            }}>
                              {log.success ? 'Success' : 'Failed'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: '#666', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.details ? JSON.stringify(log.details) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* TENANT DETAIL */}
        {page === 'tenant-detail' && selectedTenant && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <button onClick={() => setPage('tenants')} style={{
                background: 'transparent', border: `1px solid ${COLORS.border}`,
                borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer'
              }}>Back</button>
              <h1 style={{ fontSize: 24, margin: 0 }}>{selectedTenant.company_name}</h1>
              <PlanBadge plan={selectedTenant.license_plan} />
              <StatusBadge status={selectedTenant.license_status} />
            </div>

            {tenantStats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Total Loads', value: tenantStats.totalLoads, color: COLORS.primary },
                  { label: 'Total Users', value: tenantStats.totalUsers, color: COLORS.secondary },
                  { label: 'Revenue', value: formatMoney(tenantStats.totalRevenue), color: '#2d2d2d' },
                  { label: 'Margin', value: formatMoney(tenantStats.totalMargin), color: COLORS.secondary },
                ].map(card => (
                  <div key={card.label} style={{ background: COLORS.card, borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>{card.label}</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: card.color }}>{card.value}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                <h2 style={{ fontSize: 16 }}>License Details</h2>
              </div>
              <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  ['Tenant ID', selectedTenant.tenant_id],
                  ['Contact Email', selectedTenant.contact_email || '-'],
                  ['License Plan', selectedTenant.license_plan],
                  ['Max Users', selectedTenant.max_users],
                  ['Created', new Date(selectedTenant.created_at).toLocaleDateString('en-US')],
                  ['Expires', selectedTenant.expires_at ? new Date(selectedTenant.expires_at).toLocaleDateString('en-US') : 'No expiry'],
                  ['Notes', selectedTenant.notes || '-'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                <h2 style={{ fontSize: 16 }}>License renewal</h2>
              </div>
              <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13 }}>Current expiry: <b>{selectedTenant.expires_at ? new Date(selectedTenant.expires_at).toLocaleDateString('en-US') : 'No expiry'}</b> · Status: {selectedTenant.license_status}</div>
                <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)} style={{ padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13 }} />
                <button onClick={() => newExpiry && handleSetExpiry(newExpiry)} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>Set date</button>
                <button onClick={() => renewMonths(12)} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>+1 year</button>
                <button onClick={() => renewMonths(3)} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>+3 months</button>
                <button onClick={() => handleSetExpiry(null)} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>No expiry</button>
              </div>
            </div>

            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                <h2 style={{ fontSize: 16 }}>Logística (LOG-3)</h2>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Capacidades por tenant. Con ambos apagados, el comportamiento actual no cambia en nada.</div>
              </div>
              <div style={{ padding: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
                  <input type="checkbox" checked={log3Flags.require_delivery_verification}
                    onChange={e => setLog3Flags(prev => ({ ...prev, require_delivery_verification: e.target.checked }))} />
                  Verificación de entregas (reportar entrega no genera pago/factura hasta aprobar en la cola)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14 }}>
                  <input type="checkbox" checked={log3Flags.whatsapp_channel_enabled}
                    onChange={e => setLog3Flags(prev => ({ ...prev, whatsapp_channel_enabled: e.target.checked }))} />
                  Canal WhatsApp (ingesta y verificación por WhatsApp — requiere fases F2+)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={handleSaveLog3Flags} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Guardar logística</button>
                  {log3Msg && <span style={{ fontSize: 13, color: log3Msg.startsWith('Error') ? '#c0392b' : COLORS.secondary }}>{log3Msg}</span>}
                </div>
              </div>
            </div>

            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                <h2 style={{ fontSize: 16 }}>Distribución (DIST)</h2>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Capacidades de distribución por tenant. Con los flags apagados, el comportamiento actual no cambia en nada.</div>
              </div>
              <div style={{ padding: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
                  <input type="checkbox" checked={distFlags.fleet_management_enabled}
                    onChange={e => setDistFlags(prev => ({ ...prev, fleet_management_enabled: e.target.checked }))} />
                  Gestión de flota (vehículos, conductores y asignación a rutas; portal del conductor)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
                  <input type="checkbox" checked={distFlags.delivery_points_enabled}
                    onChange={e => setDistFlags(prev => ({ ...prev, delivery_points_enabled: e.target.checked }))} />
                  Puntos de venta (maestro de puntos, carga CSV, geocodificación)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
                  <input type="checkbox" checked={distFlags.route_planning_enabled}
                    onChange={e => setDistFlags(prev => ({ ...prev, route_planning_enabled: e.target.checked }))} />
                  Planificación de rutas (plantillas, import de pedidos, capacidad)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14 }}>
                  <input type="checkbox" checked={distFlags.cod_enabled}
                    onChange={e => setDistFlags(prev => ({ ...prev, cod_enabled: e.target.checked }))} />
                  COD / contra entrega (conciliación de cobros en la liquidación de ruta)
                </label>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 14 }}>
                  <span style={{ display: 'block', marginBottom: 4 }}>Radio de validación de proximidad en metros (vacío = sin validación)</span>
                  <input type="number" min="0" value={distRadius} onChange={e => setDistRadius(e.target.value)} placeholder="ej. 150"
                    style={{ width: 160, padding: '7px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13 }} />
                </label>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 14 }}>
                  <span style={{ display: 'block', marginBottom: 4 }}>Meta de SLA pedido→entrega en minutos (vacío = sin meta)</span>
                  <input type="number" min="0" value={distSla} onChange={e => setDistSla(e.target.value)} placeholder="ej. 120"
                    style={{ width: 160, padding: '7px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13 }} />
                </label>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 14 }}>
                  <span style={{ display: 'block', marginBottom: 4 }}>Línea base de entregas fallidas en % (vacío = sin línea base)</span>
                  <input type="number" min="0" step="0.1" value={distBaseline} onChange={e => setDistBaseline(e.target.value)} placeholder="ej. 5"
                    style={{ width: 160, padding: '7px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13 }} />
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={handleSaveDistFlags} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Guardar distribución</button>
                  {distMsg && <span style={{ fontSize: 13, color: distMsg.startsWith('Error') ? '#c0392b' : COLORS.secondary }}>{distMsg}</span>}
                </div>
              </div>
            </div>

            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                <h2 style={{ fontSize: 16 }}>Documental (DOC)</h2>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Requisitos documentales por cliente y cadena de frío numérica. Con los flags apagados, el comportamiento actual no cambia en nada.</div>
              </div>
              <div style={{ padding: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
                  <input type="checkbox" checked={docFlags.document_requirements_enabled}
                    onChange={e => setDocFlags(prev => ({ ...prev, document_requirements_enabled: e.target.checked }))} />
                  Requisitos documentales (catálogo, perfiles por cliente, checklist por viaje, bloqueo de cierre, reporte)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14 }}>
                  <input type="checkbox" checked={docFlags.cold_chain_enabled}
                    onChange={e => setDocFlags(prev => ({ ...prev, cold_chain_enabled: e.target.checked }))} />
                  Cadena de frío numérica (rango por viaje, lecturas en carga/entrega, alerta fuera de rango) — para modelos ≠ pharma
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button onClick={handleSaveDocFlags} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Guardar documental</button>
                  {docMsg && <span style={{ fontSize: 13, color: docMsg.startsWith('Error') ? '#c0392b' : COLORS.secondary }}>{docMsg}</span>}
                </div>
              </div>
            </div>

            {selectedTenant.business_model === 'ocean_forwarding' && (
              <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                  <h2 style={{ fontSize: 16 }}>Modos de transporte</h2>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Desactivar un modo lo oculta en la app; los datos no se borran y reaparecen al reactivarlo.</div>
                </div>
                <div style={{ padding: 24 }}>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                    {TRANSPORT_MODES.map(m => (
                      <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input type="checkbox" checked={modesSel.includes(m.key)}
                          onChange={e => setModesSel(prev => e.target.checked ? [...prev, m.key] : prev.filter(x => x !== m.key))} />
                        {m.label}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={handleSaveModes} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Guardar modos</button>
                    {modesMsg && <span style={{ fontSize: 13, color: modesMsg.startsWith('Error') ? '#c0392b' : COLORS.secondary }}>{modesMsg}</span>}
                  </div>
                  <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 18, paddingTop: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Proveedor de tracking (OF-8b)</label>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>'none' = sin ingesta automática (como hoy). Activar dispara llamadas al proveedor.</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <select value={trackingProvider} onChange={e => setTrackingProvider(e.target.value)} style={{ padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
                        <option value="none">none</option>
                        <option value="terminal49">Terminal49</option>
                        <option value="shipsgo">ShipsGo</option>
                      </select>
                      <button onClick={handleSaveTrackingProvider} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Guardar proveedor</button>
                      {trackingMsg && <span style={{ fontSize: 13, color: trackingMsg.startsWith('Error') ? '#c0392b' : COLORS.secondary }}>{trackingMsg}</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedTenant.business_model === 'ocean_forwarding' && (
              <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden', marginBottom: 24 }}>
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}` }}>
                  <h2 style={{ fontSize: 16 }}>Ocean metrics config</h2>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Thresholds for buckets, ETA alerts, empties and stale data. Changing these updates the tenant's KPIs and semaphores without code changes.</div>
                </div>
                <div style={{ padding: 24 }}>
                  <textarea value={metricsText} onChange={e => setMetricsText(e.target.value)} spellCheck={false} style={{ width: '100%', minHeight: 200, fontFamily: 'monospace', fontSize: 13, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                    <button onClick={handleSaveMetrics} style={{ background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Save config</button>
                    {metricsMsg && <span style={{ fontSize: 13, color: metricsMsg.startsWith('Error') ? '#c0392b' : COLORS.secondary }}>{metricsMsg}</span>}
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: COLORS.card, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: 16 }}>Users ({tenantUsers.length} / {selectedTenant.max_users})</h2>
                <button onClick={() => { setShowAddUser(true); setAddUserError('') }} style={{
                  background: COLORS.primary, color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}>+ Add User</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#ede9e0' }}>
                    {['Email', 'Full Name', 'Role'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: '#777', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenantUsers.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: 48, textAlign: 'center', color: '#aaa' }}>No users yet.</td></tr>
                  ) : tenantUsers.map(u => (
                    <tr key={u.email} style={{ borderTop: `1px solid ${COLORS.bg}` }}>
                      <td style={{ padding: '12px 16px' }}>{u.email}</td>
                      <td style={{ padding: '12px 16px' }}>{u.full_name || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>{u.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* MODAL AGREGAR USUARIO */}
        {showAddUser && (
          <div onClick={() => setShowAddUser(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: COLORS.card, borderRadius: 16, padding: 24,
              width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ fontSize: 18, marginBottom: 20 }}>Add User to {selectedTenant?.company_name}</h2>
              {addUserError && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                  {addUserError}
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Email *</label>
                <input type="email" value={addUserForm.email} onChange={e => setAddUserForm(f => ({ ...f, email: e.target.value }))} placeholder="user@company.com" style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Full Name</label>
                <input value={addUserForm.fullName} onChange={e => setAddUserForm(f => ({ ...f, fullName: e.target.value }))} placeholder="First Last" style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Role</label>
                <select value={addUserForm.role} onChange={e => setAddUserForm(f => ({ ...f, role: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
                  <option value="agent">Agent</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                  <option value="carrier">Carrier</option>
                </select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Password (for auto-create)</label>
                <input type="password" value={addUserForm.password} onChange={e => setAddUserForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <p style={{ fontSize: 11, color: '#888', marginBottom: 20 }}>
                Use "Auto-Create" to create the user automatically, or "Assign Existing" if the user already exists in Supabase Auth.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowAddUser(false)} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => handleAddUser(false)} disabled={addUserLoading} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${COLORS.secondary}`, color: COLORS.secondary, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Assign Existing
                </button>
                <button onClick={() => handleAddUser(true)} disabled={addUserLoading} style={{ padding: '8px 16px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {addUserLoading ? 'Creating...' : 'Auto-Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL CREAR TENANT */}
        {showCreateTenant && (
          <div onClick={() => setShowCreateTenant(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: COLORS.card, borderRadius: 16, padding: 32,
              width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ fontSize: 18, marginBottom: 24 }}>New Tenant</h2>
              {tenantFormError && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                  {tenantFormError}
                </div>
              )}
              {[
                { label: 'Company Name *', key: 'companyName', type: 'text', placeholder: 'Acme Logistics LLC' },
                { label: 'Contact Email *', key: 'contactEmail', type: 'email', placeholder: 'contact@company.com' },
                { label: 'Expiration Date', key: 'expiresAt', type: 'date', placeholder: '' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>{field.label}</label>
                  <input
                    type={field.type}
                    value={tenantForm[field.key]}
                    onChange={e => setTenantForm(f => ({ ...f, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>License Plan</label>
                <select value={tenantForm.licensePlan} onChange={e => setTenantForm(f => ({ ...f, licensePlan: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
                  <option value="basic">Basic (up to 3 users)</option>
                  <option value="professional">Professional (up to 10 users)</option>
                  <option value="enterprise">Enterprise (unlimited)</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Business Model</label>
                <select value={tenantForm.businessModel} onChange={e => setTenantForm(f => ({ ...f, businessModel: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
                  <option value="freight_broker">Freight Broker (USA-style, per-load)</option>
                  <option value="last_mile">Last Mile / LTL (package-based)</option>
                  <option value="pharma_distribution">Pharma Distribution (B2B, lot traceability)</option>
                  <option value="ocean_forwarding">Ocean Forwarding (maritime, containers/BL)</option>
                </select>
              </div>
              {tenantForm.businessModel === 'ocean_forwarding' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Modos de transporte (mínimo uno)</label>
                  <div style={{ display: 'flex', gap: 16 }}>
                    {TRANSPORT_MODES.map(m => (
                      <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input type="checkbox" checked={(tenantForm.enabledModes || []).includes(m.key)}
                          onChange={e => setTenantForm(f => {
                            const cur = f.enabledModes || []
                            return { ...f, enabledModes: e.target.checked ? [...cur, m.key] : cur.filter(x => x !== m.key) }
                          })} />
                        {m.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Max Users</label>
                <input type="number" value={tenantForm.maxUsers} onChange={e => setTenantForm(f => ({ ...f, maxUsers: Number(e.target.value) }))} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Notes</label>
                <textarea value={tenantForm.notes} onChange={e => setTenantForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowCreateTenant(false)} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleCreateTenant} disabled={tenantFormLoading} style={{ padding: '8px 16px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {tenantFormLoading ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}